import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import { createFuzzySearchIndex } from "app/pages/sales-intelligence/services/fuzzy-search/factory";
import { normalizeForSearch } from "app/pages/sales-intelligence/services/fuzzy-search/normalize";
import type { FuzzyFuseOptions, FuzzySearchHit } from "app/pages/sales-intelligence/services/fuzzy-search/types";

import { hierarchicalIndustryPathSearch } from "./hierarchicalIndustryPathSearch";
import type { IIndustrySearchIndex, IndustrySearchDoc, IndustrySearchHit, IndustrySearchParams } from "./types";

const SHORT_QUERY_THRESHOLD = 0.15;
const LONG_QUERY_THRESHOLD = 0.2;
const SHORT_QUERY_LENGTH = 3;
const MIN_UNANCHORED_QUERY_LENGTH = 3;
const MAX_RESULTS = 50;

/**
 * Fuse scores a hit as `errors / queryLength + matchPosition / distance`, so `distance` trades
 * directly against the error budget. Industry labels are multi-word, which puts the meaningful term
 * 30+ characters in ("Consumer Electronics and Appliances Retail"); at fuse's default distance that
 * position alone outweighs two real typos, and the threshold has to be loosened to compensate —
 * which is what let a different word entirely score as a match. Keeping distance large makes
 * position a ranking nudge rather than a gate, so the threshold can stay tight enough to mean
 * "roughly this word".
 */
const FUSE_OPTIONS: FuzzyFuseOptions<IndustrySearchDoc> = {
    shouldSort: true,
    distance: 1000,
    threshold: LONG_QUERY_THRESHOLD,
    includeScore: true,
    includeMatches: true,
    keys: [{ name: "name", weight: 1 }],
};

const WORD_CHARACTER = /[\p{L}\p{N}]/u;

const occursAtWordBoundary = (name: string, query: string): boolean => {
    for (let at = name.indexOf(query); at !== -1; at = name.indexOf(query, at + 1)) {
        if (at === 0 || !WORD_CHARACTER.test(name[at - 1])) {
            return true;
        }
    }

    return false;
};

/**
 * One or two characters carry no useful fuzziness — every short run of letters occurs mid-word
 * somewhere ("ai" inside "Hair", "Dairy") — so at that length a hit must start a word to count.
 */
const acceptHit = (doc: IndustrySearchDoc, normalizedQuery: string): boolean =>
    normalizedQuery.length >= MIN_UNANCHORED_QUERY_LENGTH || occursAtWordBoundary(doc.name, normalizedQuery);

/**
 * Only the node's own name is indexed, never its ancestor path. Indexing the path looks appealing
 * ("software crm" would match), but then a bare parent term matches every descendant, the match set
 * becomes the whole subtree, and revealing it mounts the entire branch.
 */
const buildDocs = (index: IndustryTaxonomyIndex, locale: string): readonly IndustrySearchDoc[] =>
    [...index.byId.values()].map((node) => ({ id: node.id, name: normalizeForSearch(node.name, locale) }));

const scoreThresholdFor = (queryLength: number): number =>
    queryLength <= SHORT_QUERY_LENGTH ? SHORT_QUERY_THRESHOLD : LONG_QUERY_THRESHOLD;

const parseIndustryPathQuery = (query: string): readonly string[] =>
    query.split("/").flatMap((segment) => {
        const trimmed = segment.trim();

        return trimmed.length > 0 ? [trimmed] : [];
    });

const toSearchHit = (hit: FuzzySearchHit<IndustryNodeId>): IndustrySearchHit => ({
    id: hit.id,
    nameRanges: hit.rangesByKey.get("name") ?? [],
});

/**
 * Fuzzy match over the taxonomy. The underlying engine caches its normalised documents on the
 * taxonomy reference, so the O(n) normalisation happens on the first keystroke rather than on page
 * load, and never again for that taxonomy.
 */
export const createIndustrySearchIndex = (): IIndustrySearchIndex => {
    const engine = createFuzzySearchIndex<IndustryTaxonomyIndex, IndustryNodeId, IndustrySearchDoc>({
        buildDocs,
        fuseOptions: FUSE_OPTIONS,
        scoreThresholdFor,
        acceptHit,
    });

    const searchHits = ({ taxonomy, query, locale }: IndustrySearchParams): readonly IndustrySearchHit[] => {
        const segments = parseIndustryPathQuery(query);

        if (segments.length === 0) {
            return [];
        }

        if (segments.length === 1) {
            return engine
                .search({ source: taxonomy, query: segments[0], locale })
                .slice(0, MAX_RESULTS)
                .map(toSearchHit);
        }

        const segmentHits = segments.map((segment) => engine.search({ source: taxonomy, query: segment, locale }));

        return hierarchicalIndustryPathSearch(taxonomy, segmentHits, MAX_RESULTS).map(toSearchHit);
    };

    return {
        match: (params) => new Set(searchHits(params).map((hit) => hit.id)),
        searchHits,
        invalidate: () => engine.invalidate(),
    };
};
